import { Box, Flex, ListItem, List, ListItemProps } from '@opendoor/bricks/core';
import { DescriptionList, DescriptionItem } from '../shared/Description';
import Tooltip from '../shared/Tooltip';
import LongText from '../shared/LongText';
import { getReadableHoaFeatures, getReadableListingStatus } from '../helpers/property';

export interface PropertyOverviewProps {
  bedrooms?: number | null;
  totalBathrooms?: number | null;
  sqFtTotalLiving?: number | null;
  sqFtLot?: number | null;
  publicRemarks?: string | null;
  monthlyHoaFee?: number | null;
  listingStatus?: string | null;
  hoaFeatures?: Array<string>;
}

const PropertyListItem: React.FC<ListItemProps & { first?: string }> = ({ first, ...props }) => (
  <ListItem
    listStyleType="none"
    display="inline-block"
    marginRight="2px"
    _before={{
      content: first ? '""' : '"   ·  "',
    }}
    {...props}
  />
);

export const PropertyOverviewBasic: React.FC<
  PropertyOverviewProps & { extras?: Array<{ value: string | null; label?: string }> }
> = ({ bedrooms, totalBathrooms, extras = [] }) => {
  return (
    <List variant="text" fontWeight="semibold" color="neutrals90">
      {bedrooms && <PropertyListItem first="true">{bedrooms} bd</PropertyListItem>}
      {totalBathrooms && <PropertyListItem>{totalBathrooms} ba</PropertyListItem>}
      {extras.reduce<Array<React.ReactNode>>((acc, { value, label }) => {
        if (!value) {
          return acc;
        }
        return acc.concat(
          <PropertyListItem key="label">
            {value} {label || ''}
          </PropertyListItem>,
        );
      }, [])}
    </List>
  );
};

const PropertyOverview: React.FC<PropertyOverviewProps> = ({
  bedrooms,
  totalBathrooms,
  sqFtTotalLiving,
  sqFtLot,
  publicRemarks,
  monthlyHoaFee,
  listingStatus,
  hoaFeatures,
}) => {
  const readableListingStatus = getReadableListingStatus(listingStatus);
  const readableHoaFeatures = getReadableHoaFeatures(monthlyHoaFee, hoaFeatures);
  return (
    <Flex flexDirection="column">
      <List
        variant="text"
        display={['block', 'block', 'none', 'none']}
        mb={6}
        fontWeight="semibold"
        color="neutrals90"
      >
        {bedrooms && <PropertyListItem first="true">{bedrooms} bd</PropertyListItem>}
        {totalBathrooms && <PropertyListItem>{totalBathrooms} ba</PropertyListItem>}
        {sqFtTotalLiving && <PropertyListItem>{sqFtTotalLiving} sq. ft</PropertyListItem>}
        {sqFtLot && <PropertyListItem>{sqFtLot} sq. ft</PropertyListItem>}
        {monthlyHoaFee && (
          <PropertyListItem>
            HOA dues {monthlyHoaFee}/mo
            {readableHoaFeatures && (
              <Tooltip analyticsName="More about HOA">{readableHoaFeatures}</Tooltip>
            )}
          </PropertyListItem>
        )}
        {listingStatus && <PropertyListItem>{readableListingStatus}</PropertyListItem>}
      </List>
      <Box display={['none', 'none', 'block', 'block']} mb={5}>
        <DescriptionList>
          <DescriptionItem label="Beds" value={bedrooms} />
          <DescriptionItem label="Baths" value={totalBathrooms} />
          <DescriptionItem
            label="Home size"
            value={sqFtTotalLiving && `${sqFtTotalLiving.toLocaleString()} sq. ft`}
          />
          <DescriptionItem
            label="Lot size"
            value={sqFtLot && `${sqFtLot.toLocaleString()} sq. ft`}
          />
          <DescriptionItem
            label="HOA dues"
            value={monthlyHoaFee && `$${monthlyHoaFee} a month`}
            tooltipContent={readableHoaFeatures}
          />
          <DescriptionItem label="Status" value={readableListingStatus} />
        </DescriptionList>
      </Box>
      {publicRemarks && (
        <Box>
          <LongText content={publicRemarks} limit={200} />
        </Box>
      )}
    </Flex>
  );
};

export default PropertyOverview;
